import Link from "next/link";
import type { ReactNode } from "react";

/*
 * Field Dossier UI primitives — the shared vocabulary of the marketing and
 * content pages. Product surfaces (board, dashboard) reuse the class strings
 * so every button on the site is drawn from the same small set.
 * Reference: docs/STYLEGUIDE.md
 */

/* ---------------------------------------------------------------- buttons */

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

const BTN_SIZE = {
  md: "h-11 px-5 text-sm",
  sm: "h-9 px-4 text-[13px]",
} as const;

const BTN_VARIANT = {
  /* Cobalt — THE action color across the site. */
  primary: "bg-brand-600 text-white hover:bg-brand-700",
  /* Alias kept for call sites that named the intent explicitly. */
  cobalt: "bg-brand-600 text-white hover:bg-brand-700",
  /* Quiet bordered action. */
  secondary:
    "border border-neutral-300 bg-white text-ink hover:border-ink hover:bg-neutral-50",
  /* Bare link-like action. */
  ghost: "text-ink hover:bg-neutral-100",
} as const;

export function buttonClass(
  variant: keyof typeof BTN_VARIANT = "primary",
  size: keyof typeof BTN_SIZE = "md",
): string {
  return `${BTN_BASE} ${BTN_SIZE[size]} ${BTN_VARIANT[variant]}`;
}

/* ----------------------------------------------------------------- labels */

/** Mono uppercase kicker with a leading tick — introduces a section. */
export function Kicker({
  children,
  tone = "brand",
}: {
  children: ReactNode;
  tone?: "brand" | "ink" | "stamp";
}) {
  const color =
    tone === "brand"
      ? "text-brand-700"
      : tone === "stamp"
        ? "text-stamp-600"
        : "text-ink-mute";
  return (
    <p className={`telex flex items-center gap-2 ${color}`}>
      <span aria-hidden className="inline-block h-[7px] w-[7px] bg-current" />
      {children}
    </p>
  );
}

/** Standard marketing section opener: kicker + display title + lede. */
export function SectionHeading({
  kicker,
  title,
  lede,
  tone = "brand",
}: {
  kicker: string;
  title: ReactNode;
  lede?: ReactNode;
  tone?: "brand" | "ink" | "stamp";
}) {
  return (
    <div className="max-w-2xl">
      <Kicker tone={tone}>{kicker}</Kicker>
      <h2 className="display mt-3 text-3xl font-semibold text-ink sm:text-4xl">
        {title}
      </h2>
      {lede ? (
        <p className="mt-4 text-lg leading-relaxed text-neutral-600">{lede}</p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ marks */

/** Circular "rubber seal" mark — decorative brand device. */
export function Seal({
  size = 96,
  className = "text-ink",
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      fill="none"
    >
      <defs>
        <path
          id="seal-arc"
          d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0"
        />
      </defs>
      <circle
        cx="60"
        cy="60"
        r="57"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <circle
        cx="60"
        cy="60"
        r="31"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <text
        fill="currentColor"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "8px",
          fontWeight: 600,
          letterSpacing: "0.14em",
        }}
      >
        <textPath href="#seal-arc">
          FINSEATS · EVERY FINANCE JOB · ONE SEARCH
        </textPath>
      </text>
      {/* Rising candlestick chart — three ascending candles with wicks */}
      <g>
        <line
          x1="47"
          y1="52"
          x2="47"
          y2="78"
          stroke="currentColor"
          strokeWidth="2"
        />
        <rect x="42.5" y="58" width="9" height="13" fill="currentColor" />
        <line
          x1="60"
          y1="45"
          x2="60"
          y2="71"
          stroke="currentColor"
          strokeWidth="2"
        />
        <rect x="55.5" y="51" width="9" height="13" fill="currentColor" />
        <line
          x1="73"
          y1="38"
          x2="73"
          y2="64"
          stroke="currentColor"
          strokeWidth="2"
        />
        <rect x="68.5" y="44" width="9" height="13" fill="currentColor" />
      </g>
    </svg>
  );
}

/** Dotted flight path with departure/arrival points. Decorative. */
export function RouteLine({ className = "" }: { className?: string }) {
  return (
    <div aria-hidden className={`flex items-center gap-2 ${className}`}>
      <span className="h-[7px] w-[7px] rounded-full border-[1.5px] border-current" />
      <span className="route-rule min-w-0 flex-1" />
      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
        <path d="M13 1 6 13l-1.4-5.2L0 6.5 13 1Z" />
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ photo */

/**
 * Editorial photo frame: hairline ink border, sharp corners, mono caption
 * bar with credit — magazine plate style.
 */
export function PhotoFrame({
  children,
  caption,
  credit,
  className = "",
}: {
  children: ReactNode;
  caption?: string;
  credit?: string;
  className?: string;
}) {
  return (
    <figure className={className}>
      <div className="overflow-hidden rounded-sm border border-ink/15 bg-neutral-100">
        {children}
      </div>
      {caption || credit ? (
        /* The caption row STACKS below sm on purpose. Side by side, the
           credit is shrink-0 (it must not break mid-word), so the row's
           min-content is the caption's min-content + the gap + the credit's
           FULL width. On a 360px phone that made the hero's grid track 370px
           and the homepage scrolled sideways by 10px. Measured 2026-09-24.
           Guard: pnpm check:mobile */
        <figcaption className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          {caption ? (
            <span className="text-[13px] leading-snug text-neutral-500">
              {caption}
            </span>
          ) : (
            <span />
          )}
          {credit ? (
            <span className="telex shrink-0 text-[9px] text-neutral-400">
              {credit}
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

/* ------------------------------------------------------------------- misc */

/** Shared page container for content pages. */
export function Container({
  children,
  className = "",
  size = "lg",
  id,
}: {
  children: ReactNode;
  className?: string;
  size?: "md" | "lg" | "xl";
  /** Anchor target (e.g. "plans" for /portfolio#plans). */
  id?: string;
}) {
  const max =
    size === "md" ? "max-w-3xl" : size === "xl" ? "max-w-6xl" : "max-w-5xl";
  return (
    <div id={id} className={`mx-auto w-full ${max} px-4 sm:px-6 ${className}`}>
      {children}
    </div>
  );
}

/** Arrow link — quiet inline CTA. */
export function ArrowLink({
  href,
  children,
  className = "",
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      href={href}
      className={`inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800 ${className}`}
    >
      {children}
      <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
        →
      </span>
    </Link>
  );
}
